import { useCallback, useState } from "react";
import { Flex, Text } from "styles/utility-components";

import { CustomerDocumentDrawer } from "components/customer_documents/CustomerDocumentDrawer";
import toast from "components/ui/toast/Toast";
import { BidReportEntry_Answer_Sources } from "lib/generated/app-service-gql/graphql";
import { useGetBidReportAnswers } from "lib/hooks/api/bid/answers/useGetBidReportAnswers";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useRestricedViewNotice } from "lib/hooks/useRestrictedRowClick";
import { AiContextProvider } from "lib/providers/AiContextProvider";
import { routes } from "lib/routes";
import { EventData, EventNames, useTracking } from "lib/tracking";
import { BidReportType } from "lib/types/graphQLEnums";
import { isNoticeSource } from "../common_report_components/DocumentSources";
import { LoadingContent } from "../common_report_components/LoadingContent";
import { QuestionAndAnswer } from "../common_report_components/QuestionAndAnswer";
import ReportCollapse from "../common_report_components/ReportCollapse";
import { AnswerSource } from "../types";
import { QualificationQuestionsSkeleton } from "./QualificationQuestionsSkeleton";
import QualificationSummary from "./QualificationSummary";

type Props = {
  bidId: string;
  bidReportId: string;
  noticeId?: string;
  noticeTitle?: string;
  hasEditAccess: boolean;
  resourceName: string;
  // Set while the qualification-report section is actively streaming. Drives
  // the spinner indicator on the questions header and the per-question
  // skeleton inside EditAnswer.
  isQualificationReportStreaming?: boolean;
  // Set while the qualification-summary section is actively streaming. Drives
  // the spinner indicator on the Summary block above the questions.
  isQualificationSummaryStreaming?: boolean;
  disableLinks?: boolean;
};
export default function QualificationQuestions({
  bidId,
  bidReportId,
  noticeId,
  noticeTitle,
  hasEditAccess,
  resourceName,
  isQualificationReportStreaming,
  isQualificationSummaryStreaming,
  disableLinks = false,
}: Props) {
  const { logEvent } = useTracking();
  const { data: currentUser } = useCurrentUser();
  const { viewRecord } = useRestricedViewNotice("Title click");

  const [isCollapseOpen, setIsCollapseOpen] = useState(false);

  const [documentData, setDocumentData] = useState<AnswerSource | null>(null);
  const [slideoutOpen] = useState(false);

  // bid orchestrator subscription has already kicked off generation of answers
  // The streamed answers land in this same cache via setQueryData and render
  // progressively until the terminal phase invalidates and re-fetches the
  // hydrated persisted shape.
  const { data, isLoading } = useGetBidReportAnswers(bidReportId);

  const handleNoticeLink = useCallback(() => {
    if (disableLinks) return;

    if (!noticeId) {
      toast.error("Failed to load notice");
      return;
    }
    if (currentUser) {
      viewRecord(noticeId);
    } else {
      window.open(`${routes.notices}/${noticeId}`, "_blank");
    }
  }, [noticeId, disableLinks, viewRecord, currentUser]);

  // TODO: Would be better to replace this and requirement summary report
  // with AI document source component. Currently there is a mismatch between
  // answer source type and document source type
  const onSelectDocument = useCallback(
    async (
      source: BidReportEntry_Answer_Sources,
      trackingContext: EventNames,
      trackingData: EventData,
    ) => {
      if (disableLinks) return;

      if (isNoticeSource(source, noticeId)) {
        handleNoticeLink();
      } else {
        if (!source.documentId) {
          toast.error("Failed to load document");
          return;
        }
        setDocumentData({
          ...source,
          documentId: source.documentId,
          locations: source.locations ?? [],
        });
      }

      logEvent(trackingContext, {
        ...trackingData,
        "Record GUID": noticeId,
        "Bid ID": bidId,
      });
    },
    [bidId, disableLinks, handleNoticeLink, logEvent, noticeId],
  );

  // The cache is shared with the orchestrator streaming pipeline, so by the time
  // the workflow has emitted its first chunk `data` is populated even though
  // persistence hasn't completed. Only show the skeleton when we genuinely
  // have nothing yet.
  if (isLoading && !data?.length) {
    return <QualificationQuestionsSkeleton />;
  }

  const qualificationQuestions = (data ?? [])
    .filter((g) => g.groupName !== "Summary")
    .flatMap((g) => g.questions);

  if (qualificationQuestions.length === 0) {
    return <Text>No qualification questions found</Text>;
  }

  return (
    <AiContextProvider
      context={{
        resourceId: bidId,
        resourceName,
        resourceType: "QUALIFICATION_REPORT",
        bidId,
      }}
    >
      <QualificationSummary
        summaryGroup={data?.find((g) => g.groupName === "Summary")}
        isStreaming={isQualificationSummaryStreaming}
        noticeId={noticeId}
      />
      <Flex column gap={24}>
        <ReportCollapse
          title="Qualification questions"
          open={isCollapseOpen}
          onOpenChange={setIsCollapseOpen}
          actions={!isCollapseOpen && isQualificationReportStreaming ? <LoadingContent /> : null}
        >
          {qualificationQuestions.map((question, idx) => (
            <QuestionAndAnswer
              question={question}
              key={question.answerId ?? idx}
              bidReportId={bidReportId}
              reportType={BidReportType.Qualification}
              debugMode={false}
              onSelectDocument={onSelectDocument}
              showDivider={false}
              slideOutOpen={slideoutOpen}
              noticeTitle={noticeTitle}
              noticeId={noticeId}
              hasEditAccess={hasEditAccess}
              hasQuestionRequalify={false}
              isStreaming={isQualificationReportStreaming}
            />
          ))}
        </ReportCollapse>
      </Flex>
      {documentData && (
        <CustomerDocumentDrawer
          source={documentData}
          isOpen={Boolean(documentData)}
          onClose={() => setDocumentData(null)}
        />
      )}
    </AiContextProvider>
  );
}
